import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Microphone, Pulse } from "@phosphor-icons/react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  recordAudio, decodeBlob, getPeaks, MixerPlayer,
  detectOnsets, estimateBPM, playCountIn, getBarSeconds,
  trimBufferTo, audioBufferToWav, getAudioContext, detectHeadphones,
} from "@/lib/audio";
import Toolbar from "./Studio/Toolbar";
import TrackRow from "./Studio/TrackRow";
import BarEditor from "./Studio/BarEditor";
import EmptyState from "./Studio/EmptyState";
import { kindOf } from "./Studio/constants";
import { useStudioActions, onsetTimes } from "./Studio/useStudioActions";

export default function Studio() {
  const { projectId } = useParams();
  const nav = useNavigate();

  const [project, setProject] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [recordingKind, setRecordingKind] = useState("rhythm");
  const [level, setLevel] = useState(0);
  const [recElapsed, setRecElapsed] = useState(0);
  const [countInBeat, setCountInBeat] = useState(0);
  // Bumped every downbeat (bar 1 of count-in + every bar start during
  // recording). Toolbar consumes this as a React `key` on the record-button
  // pulse so the CSS animation re-fires on each downbeat — the visual
  // equivalent of the metronome click so vocalists can lock in.
  const [downbeatKey, setDownbeatKey] = useState(0);
  // Monitor-while-recording: plays back existing tracks through the current
  // audio output so vocalists can overdub. Only safe on headphones — we
  // auto-detect via enumerateDevices() labels; if the browser hides labels
  // (Safari) or exposes only speakers, the toggle defaults OFF and the user
  // can flip it manually. Feedback risk on speakers, so we warn.
  const [monitorEnabled, setMonitorEnabled] = useState(false);
  const [headphoneStatus, setHeadphoneStatus] = useState({
    hasHeadphones: false, hasSpeakers: false, hasLabels: false, sample: [],
  });
  const monitorMixer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [playPos, setPlayPos] = useState(0);
  const [loopPulseKey, setLoopPulseKey] = useState(0);
  const prevPlayPosRef = useRef(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiHistory, setAiHistory] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const recCtl = useRef(null);
  const recTimer = useRef(null);
  const recAutoStop = useRef(null);
  const mixer = useRef(null);
  const rafPlay = useRef(null);
  const recStartingRef = useRef(false);

  const actions = useStudioActions({ project, setProject, projectId, tracks, setTracks });

  useEffect(() => {
    let disposed = false;
    (async () => {
      try {
        const [{ data: proj }, { data: trks }, aiHist] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/tracks`),
          api.get(`/ai/history/${projectId}`).catch(() => ({ data: { messages: [] } })),
        ]);
        if (disposed) return;
        setProject(proj);
        setAiHistory((aiHist.data.messages || []).map((m) => ({ id: m.id, role: m.role, text: m.text })));
        const loaded = await Promise.all(
          trks.map(async (t) => {
            try {
              const r = await api.get(`/files/${t.storage_path}`, { responseType: "blob" });
              const buf = await decodeBlob(r.data);
              return { ...t, buffer: buf, peaks: getPeaks(buf), onsets: onsetTimes(buf) };
            } catch (err) {
              logger.warn(`Failed to load track ${t.id}:`, err?.message || err);
              return { ...t, buffer: null, peaks: [], onsets: [] };
            }
          })
        );
        if (!disposed) setTracks(loaded);
      } catch (err) {
        logger.error("Failed to load project:", err?.message || err);
        toast.error("Project not found");
        nav("/dashboard");
      } finally {
        if (!disposed) setLoading(false);
      }
    })();
    // Cleanup — inline what `stopAll` did so effect deps stay minimal + explicit.
    return () => {
      disposed = true;
      if (recCtl.current) {
        try { recCtl.current.stop(); } catch (err) { logger.debug("rec stop:", err?.message); }
        recCtl.current = null;
      }
      if (recTimer.current) { clearInterval(recTimer.current); recTimer.current = null; }
      if (recAutoStop.current) { clearTimeout(recAutoStop.current); recAutoStop.current = null; }
      if (mixer.current) mixer.current.stop();
      if (rafPlay.current) { cancelAnimationFrame(rafPlay.current); rafPlay.current = null; }
    };
  }, [projectId, nav]);

  // Global Cmd/Ctrl+Z → Undo. Skip when the user is typing into an input/textarea/
  // contentEditable so we don't hijack native undo inside e.g. the AI prompt or
  // the BPM field.
  useEffect(() => {
    function onKey(e) {
      const isUndoCombo = (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === "z" || e.key === "Z");
      if (!isUndoCombo) return;
      const el = e.target;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      e.preventDefault();
      actions.undoLast();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actions]);

  // Downbeat flash — fires the record-button pulse on:
  //   • Count-in beat 1 (the "downbeat" of the count-in bar).
  //   • Bar 1 of recording (i.e. right when count-in ends and recording starts).
  //   • Bars 2, 3, 4 of the recording (detected via a RAF loop against the
  //     per-recording `startedAt` clock so drift is minimal).
  // Bumping `downbeatKey` remounts the pulse dot under a fresh React key
  // which restarts the CSS keyframe animation each time.
  useEffect(() => {
    if (countInBeat === 1) setDownbeatKey((k) => k + 1);
  }, [countInBeat]);
  useEffect(() => {
    if (!recording) return undefined;
    setDownbeatKey((k) => k + 1); // Bar 1 downbeat
    const barSec = getBarSeconds(project?.bpm || 120);
    const startedAt = performance.now();
    let lastBar = 0;
    let raf;
    const tick = () => {
      const elapsedSec = (performance.now() - startedAt) / 1000;
      const currentBar = Math.floor(elapsedSec / barSec);
      if (currentBar > lastBar && currentBar < (project?.loop_bars || 4)) {
        lastBar = currentBar;
        setDownbeatKey((k) => k + 1);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [recording, project?.bpm, project?.loop_bars]);

  // Headphone auto-detection. Runs once on mount + whenever the OS reports a
  // device change (plug / unplug / bluetooth pair). Auto-enables monitoring
  // if a headphone label is seen; auto-disables + locks if only speakers are
  // detected (feedback risk). Ambiguous state (Safari / no labels) leaves the
  // toggle in whatever position the user last set.
  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      const s = await detectHeadphones();
      if (cancelled) return;
      setHeadphoneStatus(s);
      if (s.hasHeadphones) setMonitorEnabled(true);
      else if (s.hasSpeakers) setMonitorEnabled(false);
    }
    refresh();
    const md = navigator.mediaDevices;
    if (md && md.addEventListener) {
      md.addEventListener("devicechange", refresh);
      return () => { cancelled = true; md.removeEventListener("devicechange", refresh); };
    }
    return () => { cancelled = true; };
  }, []);

  async function startRecording() {
    if (recording || countInBeat > 0 || recStartingRef.current) return;
    recStartingRef.current = true;
    try {
      // Pre-arm the mic + MediaRecorder BEFORE the count-in so getUserMedia
      // latency doesn't chop the first hit off the take. `.start()` becomes a
      // synchronous ~1 ms call that fires exactly at the count-in downbeat.
      const ctl = await recordAudio(setLevel, { deferStart: !!project?.count_in });
      recCtl.current = ctl;

      if (project?.count_in) {
        setCountInBeat(1);
        await playCountIn(project?.bpm || 120, 4, (beat) => setCountInBeat(beat));
        setCountInBeat(0);
        ctl.start(); // starts MediaRecorder at the beat-5 downbeat
      }
      const startedAt = Date.now();
      setRecording(true);
      setRecElapsed(0);
      recTimer.current = setInterval(() => setRecElapsed((Date.now() - startedAt) / 1000), 100);
      // Monitor mix — start looping the existing tracks through the output the
      // instant MediaRecorder starts so the recording lands sample-aligned with
      // what the vocalist is hearing. Respects current mute/solo/volume state.
      if (monitorEnabled && tracks.length > 0) {
        if (!monitorMixer.current) monitorMixer.current = new MixerPlayer();
        const loopDurSec = (project?.loop_bars || 4) * getBarSeconds(project?.bpm || 120);
        try {
          monitorMixer.current.play(tracks, { loopDuration: loopDurSec });
        } catch (err) {
          logger.warn("Monitor start failed:", err?.message || err);
        }
      }
      const loopDurMs = (project?.loop_bars || 4) * getBarSeconds(project?.bpm || 120) * 1000;
      recAutoStop.current = setTimeout(() => {
        recAutoStop.current = null;
        if (recCtl.current) {
          toast.message("Stopped at bar 4");
          stopRecording();
        }
      }, loopDurMs);
    } catch (e) {
      logger.error("Mic access:", e?.message || e);
      setCountInBeat(0);
      toast.error("Microphone access denied");
    } finally {
      recStartingRef.current = false;
    }
  }

  async function stopRecording() {
    // Guard on the ref (not the `recording` state) — setTimeout auto-stop
    // captures a stale closure where `recording` is still `false`, which
    // used to make auto-stop silently no-op past bar 4.
    if (!recCtl.current) return;
    if (recAutoStop.current) { clearTimeout(recAutoStop.current); recAutoStop.current = null; }
    // Stop the monitor mix (if any) at the same moment we stop MediaRecorder
    // so the mixer's playback trail doesn't run past the recorded take.
    if (monitorMixer.current) {
      try { monitorMixer.current.stop(); } catch (err) { logger.debug("monitor stop:", err?.message); }
    }
    const ctl = recCtl.current;
    recCtl.current = null;
    setRecording(false);
    setLevel(0);
    if (recTimer.current) { clearInterval(recTimer.current); recTimer.current = null; }
    const blob = await ctl.stop();
    if (!blob || blob.size < 200) { toast.error("Recording too short"); return; }
    // Decode → hard-trim to the exact 4-bar loop window (codec priming +
    // MediaRecorder-stop latency can push the raw take a few dozen ms past
    // the boundary, which would surface as a phantom 5th bar in Edit Bars
    // and Sync). Re-encode to WAV before upload so storage matches what we
    // decoded + trimmed.
    const rawBuf = await decodeBlob(blob);
    const loopDur = (project?.loop_bars || 4) * getBarSeconds(project?.bpm || 120);
    const buf = trimBufferTo(getAudioContext(), rawBuf, loopDur);
    const wasTrimmed = buf !== rawBuf;
    const uploadBlob = wasTrimmed ? audioBufferToWav(buf) : blob;
    let ext = "webm";
    if (wasTrimmed) ext = "wav";
    else if (blob.type.includes("mp4")) ext = "mp4";
    const fd = new FormData();
    fd.append("file", uploadBlob, `take.${ext}`);
    try {
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const trackDoc = {
        name: `${kindOf(recordingKind).l} ${tracks.filter((t) => t.kind === recordingKind).length + 1}`,
        kind: recordingKind,
        storage_path: up.path,
        duration: buf.duration,
        volume: 1, muted: false, solo: false,
        mime_type: up.content_type,
      };
      const { data: created } = await api.post(`/projects/${projectId}/tracks`, trackDoc);
      const newTrack = { ...created, buffer: buf, peaks: getPeaks(buf), onsets: onsetTimes(buf) };
      setTracks((prev) => [...prev, newTrack]);
      toast.success("Take saved");
      if ((recordingKind === "rhythm" || recordingKind === "drums") &&
          !tracks.some((t) => t.kind === "rhythm" || t.kind === "drums")) {
        const onsets = detectOnsets(buf);
        const bpm = estimateBPM(onsets);
        if (bpm && bpm !== project.bpm) {
          try {
            await actions.setBpm(bpm);
          } catch (err) {
            logger.warn("BPM auto-detect update failed:", err?.message || err);
          }
        }
      }
    } catch (e) {
      logger.error("Upload failed:", e?.message || e);
      toast.error("Upload failed");
    }
  }

  async function togglePlayback() {
    if (playing) {
      mixer.current?.stop();
      if (rafPlay.current) cancelAnimationFrame(rafPlay.current);
      setPlaying(false);
      setPlayPos(0);
      return;
    }
    if (tracks.length === 0 || tracks.every((t) => !t.buffer)) {
      toast.error("Nothing to play yet");
      return;
    }
    if (!mixer.current) mixer.current = new MixerPlayer();
    // Expose to window for QA-only programmatic inspection of sources/gains
    // during playback. Guarded so it only runs in the browser.
    if (typeof window !== "undefined") window.__mixer = mixer.current;
    const loopDur = (project?.loop_bars || 4) * getBarSeconds(project?.bpm || 120);
    await mixer.current.play(tracks, { loopDuration: loopDur });
    setPlaying(true);
    prevPlayPosRef.current = 0;
    // The mixer resolves the true loop duration from the actual buffer's
    // sample-exact length (may differ from requested `loopDur` by <1 sample
    // on non-integer BPMs); use it so the visual playhead maps 1:1 to audio.
    const activeLoopDur = mixer.current.loopDuration || loopDur;
    const loop = () => {
      const pos = mixer.current.currentPosition();
      const prev = prevPlayPosRef.current;
      // Detect loop wrap — position decreased past a safe margin (250 ms) means
      // the mixer just rolled from ~loopEnd back to ~0.
      if (prev - pos > 0.25) {
        setLoopPulseKey((k) => k + 1);
        // Visual carry-through: RAF sampling rarely catches the exact loop-end
        // moment, so the playhead visually never reaches 100% before it snaps
        // back to 0. Pin it to loop-end for one frame so bar 4 visibly
        // completes before the wrap animation runs.
        setPlayPos(activeLoopDur);
        prevPlayPosRef.current = pos;
        rafPlay.current = requestAnimationFrame(() => {
          const p2 = mixer.current.currentPosition();
          setPlayPos(p2);
          prevPlayPosRef.current = p2;
          rafPlay.current = requestAnimationFrame(loop);
        });
        return;
      }
      prevPlayPosRef.current = pos;
      setPlayPos(pos);
      rafPlay.current = requestAnimationFrame(loop);
    };
    rafPlay.current = requestAnimationFrame(loop);
  }

  // Live mute / solo / volume — while playing, any change to a track's
  // audible-state should re-thread the mixer's gain nodes so the user hears
  // the change immediately (no Stop→Play cycle required).
  useEffect(() => {
    if (playing && mixer.current) mixer.current.updateTracks(tracks);
  }, [tracks, playing]);

  async function askAi() {
    if (!aiPrompt.trim()) return;
    const msg = aiPrompt.trim();
    setAiPrompt("");
    const baseId = Date.now();
    setAiHistory((h) => [...h, { id: `u-${baseId}`, role: "user", text: msg }]);
    setAiBusy(true);
    try {
      // Feed Sound Guy the current project context — track list + key — so it
      // can reference tracks by name and give scale-aware guidance instead of
      // generic instructions.
      const context = {
        prompt: msg,
        bpm: project?.bpm || 120,
        style: "app-help",
        tracks: tracks.map((t) => ({
          name: t.name,
          kind: t.kind,
          duration: t.duration || 0,
          is_synced: !!t.is_synced,
          grid_division: t.grid_division ?? null,
        })),
        key_root: project?.key_root ?? null,
        key_mode: project?.key_mode ?? null,
        project_id: projectId,
      };
      const { data } = await api.post("/ai/suggest", context);
      setAiHistory((h) => [...h, { id: `a-${baseId}`, role: "ai", text: data.suggestion }]);
    } catch (e) {
      logger.error("AI:", e?.message || e);
      setAiHistory((h) => [...h, { id: `a-${baseId}`, role: "ai", text: "Sound Guy is unreachable right now. Try again in a moment." }]);
    } finally {
      setAiBusy(false);
    }
  }

  async function clearAiHistory() {
    try {
      await api.delete(`/ai/history/${projectId}`);
      setAiHistory([]);
    } catch (e) {
      logger.error("AI clear:", e?.message || e);
      toast.error("Couldn't clear the chat");
    }
  }

  const loopDuration = useMemo(
    () => (project?.loop_bars || 4) * getBarSeconds(project?.bpm || 120),
    [project?.bpm, project?.loop_bars]
  );
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/share/${projectId}` : "";

  if (loading) {
    return <div className="min-h-screen bb-editor grid place-items-center text-[#9CA0A6] font-mono">Loading project…</div>;
  }

  return (
    <div className="min-h-screen bb-editor flex flex-col">
      <Toolbar
        project={project}
        tracks={tracks}
        recording={recording}
        countInBeat={countInBeat}
        recElapsed={recElapsed}
        downbeatKey={downbeatKey}
        monitorEnabled={monitorEnabled}
        onToggleMonitor={setMonitorEnabled}
        headphoneStatus={headphoneStatus}
        level={level}
        recordingKind={recordingKind}
        setRecordingKind={setRecordingKind}
        onStartRecord={startRecording}
        onStopRecord={stopRecording}
        playing={playing}
        onTogglePlay={togglePlayback}
        aiOpen={aiOpen}
        setAiOpen={setAiOpen}
        aiBusy={aiBusy}
        aiPrompt={aiPrompt}
        setAiPrompt={setAiPrompt}
        aiHistory={aiHistory}
        onAskAi={askAi}
        onClearAi={clearAiHistory}
        exporting={actions.exporting}
        onExport={(fmt) => actions.exportFile(project?.name, fmt)}
        onExportStems={(ids, fmt) => actions.exportStems(project?.name, ids, fmt)}
        onToggleCountIn={actions.toggleCountIn}
        onSetLoopBars={actions.setLoopBars}
        onBpmChange={actions.changeBpm}
        shareOpen={shareOpen}
        setShareOpen={setShareOpen}
        shareUrl={shareUrl}
        onToggleShare={actions.toggleShare}
        onUndo={actions.undoLast}
        undoAvailable={actions.undoAvailable}
        onBack={() => nav("/dashboard")}
      />

      {/* Loop-wrap pulse — fires once each time the 4-bar loop rolls over.
          Absolute-positioned strip sits right under the toolbar; a keyed
          remount restarts the CSS animation cleanly on every wrap. */}
      <div className="relative">
        {loopPulseKey > 0 && (
          <div
            key={loopPulseKey}
            data-testid="loop-wrap-pulse"
            aria-hidden
            className="bb-loop-pulse pointer-events-none absolute left-0 right-0 top-0 z-10"
          >
            <div className="h-[2px] bg-[#A8FF1F]" />
            <div className="h-16 bg-gradient-to-b from-[#A8FF1F]/25 to-transparent" />
          </div>
        )}
      </div>

      {editTarget && (
        <BarEditor
          track={editTarget}
          bpm={project?.bpm || 120}
          busy={actions.busyId === editTarget.id}
          onClose={() => setEditTarget(null)}
          onApply={async (plan) => {
            await actions.applyBarEditsAndSave(editTarget, plan);
            setEditTarget(null);
          }}
        />
      )}

      <main className="flex-1 overflow-auto">
        {tracks.length === 0 ? (
          <EmptyState onRecord={startRecording} />
        ) : (
          <div data-testid="tracks-list" className="p-4 lg:p-6 space-y-3">
            {tracks.map((t) => (
              <TrackRow
                key={t.id}
                track={t}
                loopBars={project?.loop_bars || 4}
                loopDuration={loopDuration}
                playing={playing}
                playPos={playPos}
                onDelete={() => actions.deleteTrack(t.id)}
                onChange={(p) => actions.patchTrack(t.id, p)}
                onDrumify={(kit) => actions.drumify(t, kit)}
                onOneTapGroove={() => actions.oneTapGroove(t)}
                drumifying={actions.drumifyingId === t.id}
                onSync={(div) => actions.runSync(t, div)}
                syncing={actions.syncingId === t.id}
                onEdit={() => setEditTarget(t)}
                onCopySection={() => actions.copySectionAndSave(t)}
                onInstrument={(inst) => actions.replaceWithInstrument(t, inst)}
                onChordify={(inst) => actions.chordifyAndSave(t, inst)}
                onNudgeOnset={(idx, newTime) => actions.nudgeOnsetAndSave(t, idx, newTime)}
                onDeleteOnset={(idx) => actions.deleteOnsetAndSave(t, idx)}
                onDuplicateOnset={(idx) => actions.duplicateOnsetAndSave(t, idx)}
                onDuplicateToBars={(idx, bars) => actions.duplicateOnsetToBarsAndSave(t, idx, bars)}
                onClassifyOnset={(idx, kind) => actions.classifyOnsetAndSave(t, idx, kind)}
                onDeleteMany={(indices) => actions.deleteManyOnsetsAndSave(t, indices)}
                onDuplicateMany={(indices) => actions.duplicateManyOnsetsAndSave(t, indices)}
                onNudgeMany={(indices, deltaSec) => actions.nudgeManyOnsetsAndSave(t, indices, deltaSec)}
                onResizeOnset={(idx, len) => actions.resizeOnsetAndSave(t, idx, len)}
                onPitchShiftOnset={(idx, semi) => actions.pitchShiftOnsetAndSave(t, idx, semi)}
                onPitchShiftMany={(indices, semi) => actions.pitchShiftManyOnsetsAndSave(t, indices, semi)}
                onAutoTune={(indices, root, mode) => actions.autoTuneOnsetsAndSave(t, indices, root, mode)}
                onPasteAtTime={(indices, anchorTime) => actions.pasteNotesAtTimeAndSave(t, indices, anchorTime)}
                onSnapAllNotes={() => actions.snapAllNotesToGridAndSave(t)}
                bpm={project?.bpm || 120}
                busy={actions.busyId === t.id}
                keyRoot={project?.key_root ?? 0}
                keyMode={project?.key_mode ?? "major"}
                onChangeKey={actions.setKey}
              />
            ))}
            <div className="pt-4 flex items-center justify-center gap-3 flex-wrap">
              <Button
                onClick={startRecording}
                disabled={recording || countInBeat > 0}
                data-testid="add-track-btn"
                className="bb-pill h-12 px-6 bg-[#232427] border border-[#34373B] hover:bg-[#2D2F33] text-white font-semibold"
              >
                <Microphone className="mr-2" size={18} weight="fill" /> Record another layer
              </Button>
              <Button
                onClick={actions.quantizeAll}
                disabled={actions.quantizingAll || tracks.every((t) => !t.buffer || t.is_synced)}
                data-testid="quantize-all-btn"
                className="bb-pill h-12 px-6 bg-[#A8FF1F] hover:bg-[#94E600] text-[#0A0A0A] font-semibold"
              >
                <Pulse className="mr-2" size={18} weight="bold" /> {actions.quantizingAll ? "Quantizing…" : "Quantize all"}
              </Button>
              <Button
                onClick={actions.snapAllNotesInProject}
                disabled={
                  actions.snappingAll ||
                  tracks.every((t) => !t.buffer || t.is_synced || (t.onsets || []).length === 0)
                }
                data-testid="snap-all-notes-btn"
                className="bb-pill h-12 px-6 bg-[#232427] border border-[#A8FF1F] hover:bg-[#2D2F33] text-[#A8FF1F] font-semibold"
              >
                <Pulse className="mr-2" size={18} weight="bold" /> {actions.snappingAll ? "Snapping…" : "Snap all notes"}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
